import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addAppealRespondbackFeedback,
  addHigherAdminRespondbackFeedback,
  editAppealRequest2,
  editDefendantAppeal2,
  editHigherAdminFeedbackRequest2,
} from "services/appealService";
import { Roles } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  type: "defendant" | "prosecutor";
  appealId?: number;
  higherAdminFeedbackId?: number;
  respondBackTo?: string;
  onClose: (refetch: boolean) => void;
};

const HigherAdminRespondBackComment: FC<Props> = ({
  type = "prosecutor",
  appealId,
  higherAdminFeedbackId,
  respondBackTo,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState<string>("");
  const [feedbackFile, FeedbackFile] = useState<File | null>(null);

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);

  const validateForm = (tab = "all") => {
    if (!comment) return t("comment_required");
    else if (!feedbackFile) return t("feedback_file_required");

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const data = {
      actor: isProsecutor
        ? "PROSECUTOR"
        : isRegistrar
          ? "REGISTRAR"
          : isCoordinator
            ? "COORDINATOR"
            : "OTHER",
      feedback_file: feedbackFile,
      status: "RESPONDED",
      feedback_text: comment,
      appeal_request: type === "prosecutor" ? appealId || undefined : undefined,
      defendant_appeal_request:
        type === "defendant" ? appealId || undefined : undefined,

      feedback_request: higherAdminFeedbackId || undefined,

      feedback_by: loginData?.full_name || "",
      feedback_by_id: loginData?.user_id || "",
    };

    const api = appealId
      ? addAppealRespondbackFeedback
      : addHigherAdminRespondbackFeedback;
    api(data)
      .then(() => {
        if (type === "defendant" && appealId) {
          editDefendantAppeal2({
            id: appealId,
            status: "RESPONSE_PREPARED_BY_PROSECUTOR",
            coordinator_status: "RESPOND_BACK",
          });
        } else if (type === "prosecutor" && appealId) {
          editAppealRequest2({
            id: appealId,
            status: "COORDINATOR_RESPONDED_BACK",
            coordinator_status: "RESPOND_BACK",
          });
        } else if (higherAdminFeedbackId) {
          editHigherAdminFeedbackRequest2({
            id: higherAdminFeedbackId,
            status: respondBackTo,
            coordinator_status: "RESPOND_BACK",
          });
        }
        showTooltip(t("successfully_updated"), "success");
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">{t("respond_back")}</h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          {t("respond_back_detail")}
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="flex flex-col mt-4">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              {t("respond_back_feedback")}
            </label>
            <textarea
              rows={4}
              className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
          <div className="mt-4 flex flex-row gap">
            <FileUpload
              label={t("feedback_file")}
              fileTypes="*"
              required={true}
              onChange={(file) => FeedbackFile(file)}
            />
          </div>
          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                      text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                              ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  {t("loading")}
                </div>
              ) : (
                t("respond_back")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HigherAdminRespondBackComment;
